import React, { useState } from "react";

import TooltipComponent from "components/ui/tooltip/Tooltip";
import { Opportunity } from "lib/generated/app-service-gql/graphql";
import Button, { ButtonType } from "../../ui/button/Button";
import { useSaveToOpportunity } from "./hooks/useSaveToOpportunity";
import { SaveToDrawer } from "./SaveToDrawer";
import { SharedSaveProps } from "./types";

type Props = SharedSaveProps & {
  savedCount?: number;
  compactDisplay?: boolean;
  opportunities?: Pick<Opportunity, "id">[];
  saveImmediately?: boolean;
  buttonProps?: {
    buttonType?: ButtonType;
    icon?: {
      color?: string;
      fill?: string;
    };
  };
  onOpportunitySave?: (optId: string) => void;
};

type PropsWithoutDrawer = Props & {
  onShowDrawer: () => void;
};

export const SaveToButtonNoDrawer = ({
  compactDisplay = false,
  buttonProps = {
    buttonType: "primary",
    icon: {
      color: "sysTextWhite",
      fill: "sysTextWhite",
    },
  },
  savedCount,
  ...props
}: PropsWithoutDrawer) => {
  const { isSaved, totalSaved, handleSaveClick } = useSaveToOpportunity({
    ...props,
    savedCount,
    onShowDrawer: props.onShowDrawer,
  });

  return (
    <TooltipComponent content={`Save ${props.entityType}`}>
      <Button
        buttonType={buttonProps.buttonType}
        onClick={handleSaveClick}
        icon="bookmark"
        iconProps={{
          color: buttonProps.icon?.color,
          fill: isSaved ? buttonProps.icon?.fill : "transparent",
        }}
      >
        {!compactDisplay && (isSaved ? `Saved (${totalSaved})` : "Save")}
      </Button>
    </TooltipComponent>
  );
};

export function SaveToButton({
  compactDisplay = false,
  buttonProps = {
    buttonType: "primary",
    icon: {
      color: "sysTextWhite",
      fill: "sysTextWhite",
    },
  },
  savedCount,
  ...props
}: Props) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const onOpenChange = (open: boolean) => {
    setIsDrawerOpen(open);
  };

  const { isSaved, totalSaved, handleSaveClick } = useSaveToOpportunity({
    ...props,
    savedCount,
    onShowDrawer: () => setIsDrawerOpen(true),
  });

  return (
    <>
      <TooltipComponent content={`Save ${props.entityType}`}>
        <Button
          buttonType={buttonProps.buttonType}
          onClick={handleSaveClick}
          icon="bookmark"
          iconProps={{
            color: buttonProps.icon?.color,
            fill: isSaved ? buttonProps.icon?.fill : "transparent",
          }}
        >
          {!compactDisplay && (isSaved ? `Saved (${totalSaved})` : "Save")}
        </Button>
      </TooltipComponent>
      <SaveToDrawer
        {...props}
        isOpen={isDrawerOpen}
        onOpenChange={onOpenChange}
        savedCount={savedCount}
      />
    </>
  );
}
